import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bot, Check, Clock, Hand, Loader2, Lock, Play, Plus, ShieldCheck, Sparkles, X, Zap, AlertTriangle, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import api, { PLATFORM_META, STATUS_META, imageSrc } from "../lib/api";
import { useSession } from "../lib/session";
import { EngineLook } from "./EngineLook";

const inputCls = "w-full bg-slate-950/60 border border-slate-800 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-indigo-500/60 transition-colors";

const FORMATS = [
  ["viral", "Viral news cards", "A story turned into an editorial card"],
  ["static", "Single insight", "One sharp point, one image"],
  ["carousel", "Carousels", "A framework or breakdown in slides"],
];

const MODES = [
  { id: "manual", icon: Hand, title: "Ask me first", body: "Every post waits in Approvals for your tap.", plan: false },
  { id: "auto", icon: Zap, title: "Auto, with hard checks", body: "Publishes if the caption, image and slot are sound and it isn't a repeat.", plan: true },
  { id: "quality", icon: ShieldCheck, title: "Auto, quality-gated", body: "The hard checks plus an editorial score against your brand. Below the bar, it waits for you.", plan: true },
];

const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const DECISION = {
  queued: ["Sent for approval", "text-amber-300"], scheduled: ["Scheduled", "text-emerald-300"],
  held: ["Held for review", "text-amber-300"], failed: ["Failed", "text-rose-300"], draft: ["Saved as draft", "text-slate-300"],
  skipped: ["Skipped (AdSense-safe)", "text-amber-300"],
};

/* The autonomous engine's controls. Shares the page's settings object and
   Save button, so everything lives in one settings document. */
export const AutopilotEngine = ({ s, set, brands, onSave, saving, refreshBrands }) => {
  const { me } = useSession();
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [pillar, setPillar] = useState("");
  const [starting, setStarting] = useState(false);
  const allowed = me?.plan?.autopilot !== false;
  const autoAllowed = me?.plan?.auto_approve !== false;

  const loadStatus = useCallback(() => api.get("/autopilot/status").then(({ data }) => setStatus(data)).catch(() => {}), []);
  useEffect(() => {
    loadStatus();
    api.get("/accounts").then(({ data }) => setAccounts(data)).catch(() => {});
    api.get("/sources").then(({ data }) => setFeeds(data)).catch(() => {});
  }, [loadStatus]);
  // While a pass runs, follow it.
  useEffect(() => {
    if (!status?.running) return undefined;
    const t = setInterval(loadStatus, 5000);
    return () => clearInterval(t);
  }, [status?.running, loadStatus]);

  const mix = s.format_mix || { static: 30, viral: 40, carousel: 30 };
  const mixTotal = Object.values(mix).reduce((a, b) => a + Number(b || 0), 0) || 1;
  const platforms = s.autopilot_platforms || ["linkedin"];
  const pillars = s.content_pillars || [];

  const runNow = async () => {
    setStarting(true);
    try {
      await onSave();
      await api.post("/autopilot/run");
      toast.success("Autopilot is working - new posts will appear below as they finish");
      setTimeout(loadStatus, 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start a run");
    } finally {
      setStarting(false);
    }
  };

  const addPillar = (e) => {
    e.preventDefault();
    const v = pillar.trim();
    if (v && !pillars.includes(v)) set("content_pillars", [...pillars, v]);
    setPillar("");
  };

  const togglePlatform = (key) => {
    const on = platforms.includes(key);
    set("autopilot_platforms", on ? platforms.filter((p) => p !== key) : [...platforms, key]);
    if (on) set("autopilot_account_ids", (s.autopilot_account_ids || []).filter(
      (id) => accounts.find((a) => a.id === id)?.platform !== key));
  };

  const toggleAccount = (a) => {
    const ids = s.autopilot_account_ids || [];
    set("autopilot_account_ids", ids.includes(a.id) ? ids.filter((x) => x !== a.id) : [...ids, a.id]);
    if (!platforms.includes(a.platform)) set("autopilot_platforms", [...platforms, a.platform]);
  };

  return (
    <div className="relative overflow-hidden rounded-3xl border border-indigo-500/25 bg-gradient-to-br from-indigo-500/[0.08] via-slate-900/50 to-fuchsia-500/[0.05]"
      data-testid="autopilot-engine">
      <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full bg-indigo-500/15 blur-3xl" aria-hidden="true" />

      {/* Header */}
      <div className="relative p-6 sm:p-8 flex flex-col lg:flex-row lg:items-center gap-5 border-b border-white/[0.06]">
        <div className="flex items-center gap-4 flex-1">
          <div className={`relative w-14 h-14 rounded-2xl flex items-center justify-center ${s.autopilot_enabled ? "bg-indigo-500 shadow-[0_0_30px_rgba(99,102,241,0.55)]" : "bg-slate-800"}`}>
            <Bot className="w-7 h-7 text-white" />
            {status?.running && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-emerald-400 pulse-ring" />}
          </div>
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-white">Autonomous engine</h2>
            <p className="text-sm text-slate-400 mt-0.5">
              {status?.running ? "Working on new posts right now…"
                : s.autopilot_enabled ? "On - it checks every hour and keeps the calendar full."
                : "Off - turn it on and it plans, writes, designs and schedules by itself."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={runNow} disabled={starting || status?.running || !allowed}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/15 text-sm font-semibold text-white hover:bg-white/[0.06] disabled:opacity-50">
            {starting || status?.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run now
          </button>
          <button disabled={!allowed} data-testid="engine-toggle"
            onClick={() => set("autopilot_enabled", !s.autopilot_enabled)}
            className={`w-14 h-8 rounded-full p-1 transition-colors ${s.autopilot_enabled ? "bg-indigo-500" : "bg-slate-700"} disabled:opacity-50`}>
            <motion.span layout className={`block w-6 h-6 rounded-full bg-white ${s.autopilot_enabled ? "ml-auto" : ""}`} />
          </button>
        </div>
      </div>

      {!allowed && (
        <div className="relative mx-6 sm:mx-8 mt-6 flex items-center gap-3 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          <Lock className="w-4 h-4 shrink-0" /> The engine is part of Pro and Agency. You can set it up now; it runs once your plan includes it.
        </div>
      )}

      <div className="relative p-6 sm:p-8 grid gap-8 xl:grid-cols-2">
        {/* Direction */}
        <div className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Brand</label>
              <select value={s.autopilot_brand_id || ""} onChange={(e) => set("autopilot_brand_id", e.target.value || null)} className={inputCls}>
                <option value="">House style</option>
                {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Posts per week · <span className="text-white">{s.weekly_frequency}</span></label>
              <input type="range" min={1} max={21} value={s.weekly_frequency || 5}
                onChange={(e) => set("weekly_frequency", Number(e.target.value))} className="w-full accent-indigo-500 mt-2.5" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-400 mb-2 block">Format mix</label>
            <div className="space-y-3">
              {FORMATS.map(([key, label, hint]) => {
                const pct = Math.round(((Number(mix[key]) || 0) / mixTotal) * 100);
                return (
                  <div key={key} className="rounded-xl border border-slate-800 bg-slate-950/40 px-4 py-3">
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm font-semibold text-slate-200">{label}</span>
                      <span className="text-sm font-semibold text-white">{pct}%</span>
                    </div>
                    <p className="text-[11px] text-slate-500">{hint}</p>
                    <input type="range" min={0} max={100} step={5} value={Number(mix[key]) || 0}
                      onChange={(e) => set("format_mix", { ...mix, [key]: Number(e.target.value) })}
                      className="w-full accent-indigo-500 mt-2" aria-label={`${label} share`} />
                  </div>
                );
              })}
            </div>
            {s.format_mix?.carousel > 0 && (
              <div className="mt-3 flex items-center gap-3">
                <label className="text-xs text-slate-400">Slides per carousel</label>
                <input type="number" min={3} max={15} value={s.carousel_slides || 6}
                  onChange={(e) => set("carousel_slides", Math.max(3, Math.min(15, Number(e.target.value))))}
                  className="w-20 bg-slate-950/60 border border-slate-800 rounded-lg px-3 py-1.5 text-sm text-slate-100" />
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Content pillars</label>
            <p className="text-[11px] text-slate-500 mb-2">The themes it spreads posts across. Empty means it works from your brand's content philosophy.</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {pillars.map((p) => (
                <span key={p} className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-500/15 border border-indigo-500/25 px-2.5 py-1 text-xs text-indigo-100">
                  {p}
                  <button onClick={() => set("content_pillars", pillars.filter((x) => x !== p))} aria-label={`Remove ${p}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <form onSubmit={addPillar} className="flex gap-2">
              <input value={pillar} onChange={(e) => setPillar(e.target.value)} placeholder="e.g. AI in marketing" className={inputCls} />
              <button className="px-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200"><Plus className="w-4 h-4" /></button>
            </form>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={s.use_feed_trends !== false} onChange={(e) => set("use_feed_trends", e.target.checked)}
              className="accent-indigo-500 w-4 h-4 mt-0.5" />
            <span>
              <span className="block text-sm font-semibold text-slate-200">Use fresh headlines from my feeds</span>
              <span className="block text-[11px] text-slate-500">Viral slots are written from a real article fetched in the last 48 hours, when there is one.</span>
            </span>
          </label>

          {/* Optional: some of the engine's posts built straight from feed
              articles, in any format. Off at 0. */}
          <div className="rounded-xl border border-slate-800 bg-slate-950/40 px-4 py-3">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-semibold text-slate-200">Build posts from my feeds</span>
              <span className="text-sm font-semibold text-white">{s.engine_feed_share ? `${s.engine_feed_share}%` : "Off"}</span>
            </div>
            <p className="text-[11px] text-slate-500">
              The share of the engine's posts made directly from a fresh article - viral cards, single posts or
              carousels breaking the story down. The rest come from its own topics.
            </p>
            <input type="range" min={0} max={100} step={10} value={s.engine_feed_share || 0}
              onChange={(e) => set("engine_feed_share", Number(e.target.value))} className="w-full accent-indigo-500 mt-2"
              aria-label="Share of posts from feeds" data-testid="engine-feed-share" />
            {s.engine_feed_share > 0 && (
              feeds.length === 0 ? (
                <p className="text-[11px] text-amber-300 mt-1">Add a feed under News &amp; Feeds first - with none, every post comes from its own topics.</p>
              ) : (
                <div className="mt-2">
                  <p className="text-[11px] text-slate-500 mb-1.5">From which feeds (none ticked = all)</p>
                  <div className="flex flex-wrap gap-1.5">
                    {feeds.map((f) => {
                      const ids = s.engine_feed_source_ids || [];
                      const on = ids.includes(f.id);
                      return (
                        <button key={f.id} type="button"
                          onClick={() => set("engine_feed_source_ids", on ? ids.filter((x) => x !== f.id) : [...ids, f.id])}
                          className={`px-2.5 py-1 rounded-lg border text-[11px] font-semibold ${on ? "border-indigo-500 bg-indigo-500/10 text-indigo-100" : "border-slate-800 text-slate-500"}`}>
                          {f.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )
            )}
          </div>
        </div>

        {/* Where and how much to trust it */}
        <div className="space-y-6">
          <div>
            <label className="text-xs font-semibold text-slate-400 mb-2 block">Publish to</label>
            <div className="flex flex-wrap gap-2">
              {Object.entries(PLATFORM_META).map(([key, meta]) => {
                const on = platforms.includes(key);
                return (
                  <button key={key} onClick={() => togglePlatform(key)}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-semibold ${on ? "text-white" : "border-slate-800 text-slate-400"}`}
                    style={on ? { background: `${meta.color}22`, borderColor: meta.color } : {}}>{meta.label}</button>
                );
              })}
            </div>
            {accounts.filter((a) => platforms.includes(a.platform)).length > 0 && (
              <div className="mt-3">
                <p className="text-[11px] text-slate-500 mb-1.5">Specific accounts (none ticked = every connected account on those channels)</p>
                <div className="flex flex-wrap gap-2">
                  {accounts.filter((a) => platforms.includes(a.platform)).map((a) => {
                    const on = (s.autopilot_account_ids || []).includes(a.id);
                    return (
                      <button key={a.id} onClick={() => toggleAccount(a)}
                        className={`px-2.5 py-1 rounded-lg border text-[11px] font-semibold ${on ? "border-indigo-500 bg-indigo-500/10 text-indigo-100" : "border-slate-800 text-slate-500"}`}>
                        {a.display_name}{a.account_kind === "page" && <span className="ml-1 text-[9px] px-1 rounded bg-sky-500/15 text-sky-300">Page</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-400 mb-2 block">How much to trust it</label>
            <div className="space-y-2">
              {MODES.map(({ id, icon: Icon, title, body, plan }) => {
                const active = (s.approval_mode || "manual") === id;
                const locked = plan && !autoAllowed;
                return (
                  <button key={id} disabled={locked} onClick={() => set("approval_mode", id)}
                    className={`w-full text-left flex items-start gap-3 rounded-xl border px-4 py-3 transition-colors ${
                      active ? "border-indigo-400/60 bg-indigo-500/10" : "border-slate-800 hover:border-slate-700"} disabled:opacity-50`}>
                    <Icon className={`w-4 h-4 mt-0.5 ${active ? "text-indigo-300" : "text-slate-500"}`} />
                    <span className="flex-1">
                      <span className="flex items-center gap-2 text-sm font-semibold text-slate-100">
                        {title} {locked && <Lock className="w-3 h-3 text-slate-500" />}
                      </span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">{body}</span>
                    </span>
                    {active && <Check className="w-4 h-4 text-indigo-300" />}
                  </button>
                );
              })}
            </div>
            {s.approval_mode === "quality" && (
              <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950/40 px-4 py-3">
                <div className="flex justify-between text-xs"><span className="text-slate-400">Quality bar</span><span className="text-white font-semibold">{s.quality_threshold || 7} / 10</span></div>
                <input type="range" min={5} max={9} value={s.quality_threshold || 7}
                  onChange={(e) => set("quality_threshold", Number(e.target.value))} className="w-full accent-indigo-500 mt-2" />
                <p className="text-[11px] text-slate-500 mt-1">Higher holds more posts for you. 7 is a sensible start.</p>
              </div>
            )}
          </div>

          <button onClick={onSave} disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-semibold disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Save engine settings
          </button>
        </div>
      </div>

      <div className="relative px-6 sm:px-8 pb-8">
        <EngineLook brandId={s.autopilot_brand_id} brands={brands} workspaceName={me?.workspace?.name}
          refreshBrands={refreshBrands}
          onBrandCreated={async (id) => {
            // Saved at once, not left in the unsaved form: the theme or logo
            // just written to this brand must not be orphaned if the page is
            // left without pressing Save.
            set("autopilot_brand_id", id);
            const payload = { ...s, autopilot_brand_id: id };
            delete payload.id;
            await api.put("/settings", payload);
            await refreshBrands();
          }} />
      </div>

      {/* What it's doing */}
      {status && (
        <div className="relative border-t border-white/[0.06] p-6 sm:p-8 grid gap-8 xl:grid-cols-[1.4fr_1fr]">
          {/* min-w-0: grid children default to their content's width, which let
              long topic lines push past the card instead of truncating. */}
          <div className="min-w-0">
            <div className="flex items-baseline justify-between mb-3">
              <h3 className="font-heading font-bold text-slate-100">Coming up</h3>
              <span className="text-xs text-slate-500">{status.published_30d} published in the last 30 days · {status.held} waiting for you</span>
            </div>
            {status.upcoming.length === 0 ? (
              <p className="text-sm text-slate-500 rounded-xl border border-dashed border-slate-800 py-8 text-center">Nothing planned yet. Turn it on, or press Run now.</p>
            ) : (
              <div className="space-y-2">
                <AnimatePresence initial={false}>
                  {status.upcoming.slice(0, 8).map((p) => {
                    const src = imageSrc(p) || imageSrc(p.slides?.[0]);
                    const st = STATUS_META[p.status] || { label: p.status, cls: "text-slate-400 border-slate-700" };
                    return (
                      <motion.div key={p.id} layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-950/40 p-2.5">
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-900 shrink-0 flex items-center justify-center">
                          {src ? <img src={src} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-4 h-4 text-slate-600" />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-100 truncate">{p.topic}</p>
                          <p className="text-[11px] text-slate-500 flex items-center gap-1.5">
                            <span className="capitalize">{p.fmt}</span> · <Clock className="w-3 h-3" /> {when(p.scheduled_at)}
                          </p>
                          {p.hold_reasons?.length > 0 && (
                            <p className="text-[11px] text-amber-300/90 truncate flex items-center gap-1"><AlertTriangle className="w-3 h-3 shrink-0" /> {p.hold_reasons[0]}</p>
                          )}
                        </div>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </div>

          <div className="min-w-0">
            <h3 className="font-heading font-bold text-slate-100 mb-3">Recent passes</h3>
            {status.runs.length === 0 ? (
              <p className="text-sm text-slate-500">No passes yet.</p>
            ) : (
              <div className="space-y-2">
                {status.runs.slice(0, 6).map((r) => (
                  <div key={r.id} className="rounded-xl border border-slate-800 bg-slate-950/40 px-3.5 py-2.5">
                    <div className="flex justify-between text-[11px] text-slate-500">
                      <span>{when(r.at)} · {r.trigger === "manual" ? "run by hand" : "scheduled"}</span>
                      <span>{r.created?.length || 0} made</span>
                    </div>
                    {r.error && <p className="mt-1 text-xs text-rose-300">{r.error}</p>}
                    {r.skipped && <p className="mt-1 text-xs text-slate-400">{r.skipped}</p>}
                    {(r.decisions || []).map((d, i) => {
                      const [label, tone] = DECISION[d.decision] || [d.decision, "text-slate-300"];
                      return (
                        <p key={i} className="mt-1 text-xs text-slate-300 truncate">
                          <span className={`font-semibold ${tone}`}>{label}</span> · {d.topic}
                        </p>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
